import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// NEU: Typen importieren
import { Buyer } from '../../types/invoiceTypes';
import { db } from '../../firebaseConfig';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc, serverTimestamp, setDoc, getFirestore } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import logger, { nativeConsole } from '../../utils/logger';
import { deepEqual } from '../../utils/objectUtils';
import { User } from 'firebase/auth';

// Lokales Interface entfernen
/*
interface Buyer {
  name: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  // Optional später: id, street2, reference, communication
}
*/

// VB-1 Feature-Flag (build-time, fail-closed, Default AUS).
const TAX_CATEGORY_ENABLED = process.env.REACT_APP_TAX_CATEGORY_ENABLED === 'true';

// Erweiterte Props
interface BuyerDetailsFormProps {
  buyer: Buyer; 
  setBuyer: (buyer: Buyer) => void;
  onCustomerSelect?: (selectedCustomer: Buyer, selectedCustomerId: string) => void;
  requiresLeitwegId?: boolean; // Neuer prop für XRechnung
  showTitle?: boolean;
}

// Für Typsicherheit
interface CustomerSuggestion {
  firestoreDocId: string;
  customerId?: string;
  companyName?: string;
  personName?: string;
  name?: string;
  id?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  street2?: string | null;
  reference?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  [key: string]: any; // Für andere mögliche Felder
}

// Autocomplete-Hook für Kunden
function useFirestoreAutocompleteCustomers(field: 'companyName' | 'personName', searchValue: string) {
  const { currentUser: user } = useAuth();
  const [results, setResults] = React.useState<CustomerSuggestion[]>([]);
  React.useEffect(() => {
    if (!user || !searchValue || searchValue.length < 2) { setResults([]); return; }
    const fetch = async () => {
      try {
        const col = collection(db, 'customers');
        const q = query(
          col, 
          where('userId', '==', user.uid),
          orderBy(field), 
          limit(100) // Großzügiges Limit für bessere Durchsuchbarkeit
        );
        
        const snap = await getDocs(q);
        
        // Client-seitige Filterung für verbesserte Suchqualität
        const filteredResults = snap.docs
          .map(doc => ({ firestoreDocId: doc.id, ...doc.data() } as CustomerSuggestion)) // Wichtig: Jetzt speichern wir doc.id als firestoreDocId
          .filter(item => {
            const fieldValue = item[field]?.toString() || '';
            // Prüfe auf Teilstring-Übereinstimmung (case-insensitive)
            return fieldValue.toLowerCase().includes(searchValue.toLowerCase());
          })
          .slice(0, 7); // Beschränke auf 7 Ergebnisse
          
        setResults(filteredResults);
      } catch (error) {
        logger.error(error, "fehler beim abrufen der käufer-vorschläge");
        setResults([]);
      }
    };
    fetch();
  }, [user, searchValue, field]);
  return results;
}

// CustomerData Typ definieren
interface CustomerData {
  companyName: string;
  personName: string;
  street: string;
  street2: string;
  zip: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  reference: string;
  userId: string;
}

const BuyerDetailsForm: React.FC<BuyerDetailsFormProps> = ({ buyer, setBuyer, onCustomerSelect, requiresLeitwegId, showTitle = true }) => {
  const { t } = useTranslation('invoice');
  const { currentUser } = useAuth();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(buyer?.customerId || null);
  const [customerSuggestions, setCustomerSuggestions] = useState<CustomerSuggestion[]>([]);
  const [initialBuyerSet, setInitialBuyerSet] = useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [companyNameInput, setCompanyNameInput] = React.useState(buyer.companyName || "");
  const [personNameInput, setPersonNameInput] = React.useState(buyer.personName || "");
  const [showCompanyDropdown, setShowCompanyDropdown] = React.useState(false);
  const [showPersonDropdown, setShowPersonDropdown] = React.useState(false);
  const companySuggestions = useFirestoreAutocompleteCustomers('companyName', companyNameInput);
  const personSuggestions = useFirestoreAutocompleteCustomers('personName', personNameInput);
  
  // Speichere ursprüngliche Werte
  const [originalBuyer, setOriginalBuyer] = React.useState<Buyer | null>(null);

  // **FIX 1: Timeout-Referenzen für Cleanup**
  const companyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const personTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // **FIX 2: Cleanup-Funktion für alle Timeouts**
  useEffect(() => {
    return () => {
      if (companyTimeoutRef.current) {
        clearTimeout(companyTimeoutRef.current);
      }
      if (personTimeoutRef.current) {
        clearTimeout(personTimeoutRef.current);
      }
    };
  }, []);

  // Initialize originalBuyer when buyer prop changes (e.g., loaded from offer)
  useEffect(() => {
    if (buyer && !originalBuyer) { // Set original only once initially or when buyer changes significantly
      setOriginalBuyer(JSON.parse(JSON.stringify(buyer)));
      setSelectedCustomerId(buyer?.customerId || null); // Also set initial customerId
      setCompanyNameInput(buyer.companyName || "");
      setPersonNameInput(buyer.personName || "");
    }
  }, [buyer]); // Dependency on buyer prop
  
  // Neuer useEffect für die Aktualisierung der Input-Felder bei Änderungen der buyer-Props
  useEffect(() => {
    // Aktualisiere die Input-Felder, wenn sich die relevanten buyer-Eigenschaften ändern
    setCompanyNameInput(buyer.companyName || "");
    setPersonNameInput(buyer.personName || "");
    

  }, [buyer.companyName, buyer.personName]); // Reagiere auf Änderungen dieser spezifischen Eigenschaften

  // Helper to update buyer state
  const updateBuyer = (field: keyof Buyer, value: string | null) => {
    const updatedBuyer = { ...buyer };
    if (field === 'id') {
      updatedBuyer.id = value && value.trim() !== '' ? value : undefined;
    } else if (
      field === 'name' ||
      field === 'companyName' ||
      field === 'personName' ||
      field === 'street' ||
      field === 'zip' ||
      field === 'city' ||
      field === 'country' ||
      field === 'street2' ||
      field === 'reference' ||
      field === 'email' ||
      field === 'phone' ||
      field === 'website' ||
      field === 'vatNumber'
    ) {
      (updatedBuyer as any)[field] = value || '';
    } else if (field === 'customerId') {
      // Bei manueller Änderung der Kundennummer im Formular
      updatedBuyer.customerId = value || '';
    }
    
    // name-Feld für Kompatibilität generieren
    if (updatedBuyer.companyName && updatedBuyer.companyName.trim() !== "") {
      updatedBuyer.name = updatedBuyer.companyName;
    } else if (updatedBuyer.personName && updatedBuyer.personName.trim() !== "") {
      updatedBuyer.name = updatedBuyer.personName;
    } else {
      updatedBuyer.name = "";
    }

    // Buyer aktualisieren
    setBuyer(updatedBuyer);
  };

  // Validierung: Mindestens eines der beiden Felder muss ausgefüllt sein
  React.useEffect(() => {
    if ((buyer.companyName && buyer.companyName.trim() !== "") || (buyer.personName && buyer.personName.trim() !== "")) {
      setError(null);
    } else {
                  setError(t('buyer.nameRequired'));
    }
  }, [buyer.companyName, buyer.personName, t]);

  // Aktualisiere die logCustomerFound-Funktion, um den native console.log zu verwenden
  const logCustomerFound = (customer: any) => {
    console.log("Kunde aus DB gefunden:", customer);
  };

  // **FIX 3: Sichere Timeout-Handler mit Cleanup**
  const handleCompanyBlur = () => {
    if (companyTimeoutRef.current) {
      clearTimeout(companyTimeoutRef.current);
    }
    companyTimeoutRef.current = setTimeout(() => {
      setShowCompanyDropdown(false);
    }, 150);
  };

  const handlePersonBlur = () => {
    if (personTimeoutRef.current) {
      clearTimeout(personTimeoutRef.current);
    }
    personTimeoutRef.current = setTimeout(() => {
      setShowPersonDropdown(false);
    }, 150);
  };

  return (
    <div className={showTitle ? "p-4 border border-hairline rounded-md shadow-sm bg-surface-raised mb-6" : ""}>
      {showTitle && <h2 className="text-xl font-semibold mb-4 text-primary">{t('buyer.data')}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Firmenname (optional) mit Autocomplete */}
        <div className="md:col-span-2 relative">
          <label htmlFor="buyerCompanyName" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.companyName')}
          </label>
          <input
            type="text"
            id="buyerCompanyName"
            name="companyName"
            value={companyNameInput}
            onChange={e => { setCompanyNameInput(e.target.value); updateBuyer("companyName", e.target.value || null); setShowCompanyDropdown(true); }}
            onFocus={() => setShowCompanyDropdown(true)}
            onBlur={handleCompanyBlur}
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.companyNamePlaceholder')}
            autoComplete="off"
          />
          {showCompanyDropdown && companySuggestions.length > 0 && (
            <ul className="absolute z-10 bg-surface-inset border border-strong rounded shadow w-full mt-1 max-h-48 overflow-auto">
              {companySuggestions.map((s, index) => (
                <li key={`company-${s.firestoreDocId}-${index}`} className="px-3 py-2 text-primary hover:bg-surface-hover cursor-pointer" onMouseDown={() => {
                  // **FIX 4: Cleanup timeout beim Klick**
                  if (companyTimeoutRef.current) {
                    clearTimeout(companyTimeoutRef.current);
                  }
                  
                  const firestoreDocId = s.firestoreDocId;
                  nativeConsole.log(`Kunde aus Autocomplete ausgewählt, Firestore-ID: ${firestoreDocId}`);
                  
                  // Construct the selected customer object from suggestion `s`
                  const selectedCustomerData: Buyer = {
                    name: s.name || s.companyName || s.personName || '', // Ensure name is set
                    companyName: s.companyName || null,
                    personName: s.personName || null,
                    street: s.street || '',
                    zip: s.zip || '',
                    city: s.city || '',
                    country: s.country || 'DE',
                    id: s.id || null, // User-defined ID
                    street2: s.street2 || null,
                    reference: s.reference || null,
                    communication: [], // Assuming communication is not in suggestion
                    customerId: s.customerId || null, // System customerId if present
                    email: s.email || null, // Add email if available
                    phone: s.phone || null, // Add phone if available
                    website: s.website || null // Add website if available
                  };
                  
                  // Set local state for UI and original buyer tracking
                  setSelectedCustomerId(firestoreDocId); 
                  setOriginalBuyer(JSON.parse(JSON.stringify(selectedCustomerData))); // Store for comparison
                  setBuyer(selectedCustomerData); // Update the main form state
                  setCompanyNameInput(selectedCustomerData.companyName || "");
                  setPersonNameInput(selectedCustomerData.personName || "");
                  setShowCompanyDropdown(false);
                  
                  // Call the callback passed from the parent hook
                  if (onCustomerSelect) {
                    onCustomerSelect(selectedCustomerData, firestoreDocId);
                  }
                }}>
                  <div className="flex flex-col">
                    <span className="font-medium">{s.companyName || s.name || ""}</span>
                    <div className="flex justify-between text-xs text-secondary">
                      <span>{s.personName ? s.personName : ""}</span>
                      <span>{s.city ? `${s.zip || ""} ${s.city}` : ""}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Vor-/Nachname (optional) mit Autocomplete */}
        <div className="md:col-span-2 relative">
          <label htmlFor="buyerPersonName" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.fullName')}
          </label>
          <input
            type="text"
            id="buyerPersonName"
            name="personName"
            value={personNameInput}
            onChange={e => { setPersonNameInput(e.target.value); updateBuyer("personName", e.target.value || null); setShowPersonDropdown(true); }}
            onFocus={() => setShowPersonDropdown(true)}
            onBlur={handlePersonBlur}
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.fullNamePlaceholder')}
            autoComplete="off"
          />
          {showPersonDropdown && personSuggestions.length > 0 && (
            <ul className="absolute z-10 bg-surface-inset border border-strong rounded shadow w-full mt-1 max-h-48 overflow-auto">
              {personSuggestions.map((s, index) => (
                <li key={`person-${s.firestoreDocId}-${index}`} className="px-3 py-2 text-primary hover:bg-surface-hover cursor-pointer" onMouseDown={() => {
                  // **FIX 5: Cleanup timeout beim Klick**
                  if (personTimeoutRef.current) {
                    clearTimeout(personTimeoutRef.current);
                  }
                  
                  const firestoreDocId = s.firestoreDocId;
                  nativeConsole.log(`Kunde aus Autocomplete ausgewählt, Firestore-ID: ${firestoreDocId}`);
                  
                  const selectedCustomerData: Buyer = {
                    name: s.name || s.companyName || s.personName || '',
                    companyName: s.companyName || null,
                    personName: s.personName || null,
                    street: s.street || '',
                    zip: s.zip || '',
                    city: s.city || '',
                    country: s.country || 'DE',
                    id: s.id || null,
                    street2: s.street2 || null,
                    reference: s.reference || null,
                    communication: [],
                    customerId: s.customerId || null,
                    email: s.email || null,
                    phone: s.phone || null,
                    website: s.website || null
                  };

                  setSelectedCustomerId(firestoreDocId);
                  setOriginalBuyer(JSON.parse(JSON.stringify(selectedCustomerData)));
                  setBuyer(selectedCustomerData);
                  setCompanyNameInput(selectedCustomerData.companyName || "");
                  setPersonNameInput(selectedCustomerData.personName || "");
                  setShowPersonDropdown(false);

                  if (onCustomerSelect) {
                    onCustomerSelect(selectedCustomerData, firestoreDocId);
                  }
                }}>
                  <div className="flex flex-col">
                    <span className="font-medium">{s.personName || s.name || ""}</span>
                    <div className="flex justify-between text-xs text-secondary">
                      <span>{s.companyName ? `${s.companyName}` : ""}</span>
                      <span>{s.city ? `${s.zip || ""} ${s.city}` : ""}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Fehlermeldung */}
        {error && (
          <div className="md:col-span-2">
            <p className="text-xs text-[var(--tone-danger-fg)] mt-1">{error}</p>
          </div>
        )}
        {/* Käufer-ID (optional) */}
        <div>
           <label htmlFor="buyerId" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.buyerId')}
          </label>
          <input
            type="text"
            id="buyerId"
            name="id"
            value={buyer.id || ''}
            onChange={(e) => updateBuyer("id", e.target.value || null)}
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.buyerIdPlaceholder')}
          />
        </div>
        {/* Käufer-Kundennummer (optional) */}
        <div>
          <label htmlFor="buyerCustomerId" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.customerNumber')}
          </label>
          <input
            type="text"
            id="buyerCustomerId"
            name="customerId"
            value={buyer.customerId || ''}
            onChange={e => updateBuyer("customerId", e.target.value || null)}
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.customerNumberPlaceholder')}
          />
        </div>
        {/* VB-1: Käufer-USt-IdNr. (BT-48) — Pflicht bei §6a IGL / §13b (nur bei aktivem Flag). */}
        {TAX_CATEGORY_ENABLED && (
          <div>
            <label htmlFor="buyerVatNumber" className="block text-sm font-medium text-secondary mb-1">
              {t('buyer.vatId')}
            </label>
            <input
              type="text"
              id="buyerVatNumber"
              name="vatNumber"
              value={buyer.vatNumber || ''}
              onChange={e => updateBuyer("vatNumber", e.target.value || null)}
              className="input-field placeholder-visible"
              placeholder={t('taxCategory.buyerVatPlaceholder')}
            />
          </div>
        )}
        {/* Straße */}
        <div className="md:col-span-2">
          <label htmlFor="buyerStreet" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.street')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="buyerStreet"
            name="street"
            value={buyer.street}
            onChange={(e) => updateBuyer("street", e.target.value)}
            required
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.streetPlaceholder')}
          />
        </div>
        {/* Adresszusatz (street2) */}
        <div className="md:col-span-2">
          <label htmlFor="buyerStreet2" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.addressSupplement')}
          </label>
          <input
            type="text"
            id="buyerStreet2"
            name="street2"
            value={buyer.street2 || ''}
            onChange={(e) => updateBuyer("street2", e.target.value || null)}
            className="input-field placeholder-visible"
                                        placeholder={t('buyer.addressSupplementPlaceholder')}
          />
        </div>
        {/* PLZ & Stadt - Responsive Grid */}
        <div className="md:col-span-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* PLZ */}
            <div>
              <label htmlFor="buyerZip" className="block text-sm font-medium text-secondary mb-1">
                                        {t('buyer.postalCode')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="buyerZip"
                name="zip"
                value={buyer.zip}
                onChange={(e) => updateBuyer("zip", e.target.value)}
                required
                className="input-field placeholder-visible"
                placeholder="12345"
              />
            </div>
            {/* Stadt */}
            <div className="sm:col-span-2">
              <label htmlFor="buyerCity" className="block text-sm font-medium text-secondary mb-1">
                                        {t('buyer.city')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="buyerCity"
                name="city"
                value={buyer.city}
                onChange={(e) => updateBuyer("city", e.target.value)}
                required
                className="input-field placeholder-visible"
                                            placeholder={t('buyer.cityPlaceholder')}
              />
            </div>
          </div>
        </div>
        {/* Land */}
        <div>
          <label htmlFor="buyerCountry" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.country')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="buyerCountry"
            name="country"
            value={buyer.country}
            onChange={(e) => updateBuyer("country", e.target.value.toUpperCase())}
            required
            maxLength={2}
            className="input-field placeholder-visible uppercase"
            placeholder="DE"
          />
        </div>
        {/* Käuferreferenz (z.B. Besteller) - angepasst für XRechnung */}
        <div>
          <label htmlFor="buyerReference" className="block text-sm font-medium text-secondary mb-1">
            {requiresLeitwegId ? (
                                      <>{t('buyer.leitwegIdLabel')} <span className="text-red-500">*</span></>
            ) : (
                                  <>{t('buyer.yourReference')}</>
            )}
          </label>
          <input
            type="text"
            id="buyerReference"
            name="reference"
            value={buyer.reference || ''}
            onChange={(e) => updateBuyer("reference", e.target.value || null)}
            required={requiresLeitwegId}
            className="input-field placeholder-visible"
                            placeholder={requiresLeitwegId ? t('buyer.leitwegIdPlaceholder') : t('buyer.yourReferencePlaceholder')}
          />
          {requiresLeitwegId && (
            <p className="mt-1 text-xs text-muted">
                              {t('buyer.leitwegIdHelp')}
            </p>
          )}
        </div>
        
        {/* Neue Felder hinzufügen: E-Mail, Telefon, Website */}
        {/* E-Mail (Optional für normale Rechnungen, Pflicht für XRechnung) */}
        <div className="md:col-span-2">
          <label htmlFor="buyerEmail" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.email')} {requiresLeitwegId ? <span className="text-red-500">*</span> : ""}
          </label>
          <input
            type="email"
            id="buyerEmail"
            name="email"
            value={buyer.email || ''}
            onChange={(e) => updateBuyer("email", e.target.value || null)}
            required={requiresLeitwegId}
            className="input-field placeholder-visible"
                          placeholder={t('buyer.emailPlaceholder')}
          />
          {requiresLeitwegId && (
            <p className="mt-1 text-xs text-muted">
                              {t('buyer.emailRequiredHelp')}
            </p>
          )}
        </div>
        
        {/* Telefon (Optional) */}
        <div>
          <label htmlFor="buyerPhone" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.phone')}
          </label>
          <input
            type="tel"
            id="buyerPhone"
            name="phone"
            value={buyer.phone || ''}
            onChange={(e) => updateBuyer("phone", e.target.value || null)}
            className="input-field placeholder-visible"
            placeholder="+49 000 000000"
          />
        </div>
        
        {/* Website (Optional) */}
        <div>
          <label htmlFor="buyerWebsite" className="block text-sm font-medium text-secondary mb-1">
                                    {t('buyer.website')}
          </label>
          <input
            type="text"
            id="buyerWebsite"
            name="website"
            value={buyer.website || ''}
            onChange={(e) => updateBuyer("website", e.target.value || null)}
            className="input-field placeholder-visible"
            placeholder="https://example.com"
          />
        </div>
      </div>
    </div>
  );
};

export default BuyerDetailsForm; 